import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import Modal from '../Modal';
import { FormErrorBanner } from '../FormErrorBanner';
import { InlineLoader } from '../loading';
import SearchableSelect from '../SearchableSelect';
import {
  hiringLinksAPI,
  type HiringBudget,
  type HiringLink,
  type HiringLinkInput,
  type HiringPickerOpening,
} from '../../services/api';
import {
  DEFAULT_POSITIONS_PER_OPENING,
  HIRING_CODES,
  OPENING_ROW_WORDS,
  RAISE_CODES,
  hiringCodeOf,
  hiringErrorText,
  hiringStatusOf,
  isHiringOff,
  isNoRecruitmentIdentity,
  openingAddress,
  openingNames,
  openingPlaceCell,
  openingRounds,
  pickState,
  positionsPerOpeningOf,
  refusedOpeningTag,
  sharedLine,
} from './hiringText';
import {
  LINK_WORDS, linkChosenHover, linkChosenTeam, linkOpeningMatches, linkOpeningRank, openingTagWord,
} from './hiringText.dashboard';
import { LinkStorePicker, linkStoreLabel, type LinkStore } from './LinkStorePicker';

/**
 * Link an opening (approved mock-up of 15 Oct 2026, panel 6): follow an opening that ALREADY
 * EXISTS in Recruitment, for a store or with no store. Nothing is sent to Recruitment - the
 * window reads the project's budgets and one budget's openings, and saves the link in
 * ChannelPlex.
 *
 * Store → Budget → the budget's openings → Link. Since 16 Oct 2026 (approved option A, after the
 * product owner turned down one card per opening) the openings are a comparison table the
 * person picks a row of (radio semantics): Address | City | State | Open (what it means on its
 * (i)) | Recruiters | Offer makers | Interviewers - openings can share an address, a city and a
 * State and differ only in their team, so that is what the person compares. The opening's number
 * is not a column: it is on the row's hover, in the radio's name and in what is sent. Openings
 * that can be linked with a position open come first. A search looks in the address, city,
 * State and the names. Every opening is drawn (the table scrolls): nothing is capped. The
 * window is the widest Modal ('2xl') so the seven columns fit a laptop; on a phone the table
 * scrolls sideways in its own box.
 *
 * Opened from a store's row, the store is fixed. Opened from the Hiring dashboard - since 16 Oct
 * 2026 only through "Link it instead" in a raise's review - the store is an optional, searchable picker (the stores within this
 * person's reach), and with none the opening is followed with no store: the server names it
 * after its budget. No role name, no reason and no note (product owner, 14 and 15 October 2026).
 *
 * The server decides whether an opening may be linked and its refusal is shown as it is. The
 * rows it would always refuse are greyed here first, with a short word and the reason on
 * hover, so a person is not offered a choice only to be told no.
 */

export type LinkHiringSubject =
  | { kind: 'store'; storeId: string; storeName: string; storeCode: string }
  /** Opened from the Hiring dashboard: the store is optional, and chosen in the window. */
  | { kind: 'role' };

/** What the window opens on, from "Link it instead" in the Raise window. */
export interface LinkHiringInitial {
  budgetId: number;
  openingId: number;
  /** The store the Raise window had, when it had one. */
  store?: LinkStore | null;
  /** No longer asked for: a hiring with no store is named after its budget. Ignored. */
  roleName?: string;
}

/** Refusals that mean the openings on show are out of date. */
const REREAD_OPENINGS_ON = new Set<string>([
  HIRING_CODES.alreadyLinked, HIRING_CODES.sharedFull, HIRING_CODES.notInBudget, HIRING_CODES.noOpenPosition,
  HIRING_CODES.severalPositions, HIRING_CODES.sharedNotAllowed,
  // It may be the opening a request is raising: the fresh read marks it so.
  RAISE_CODES.openingBeingRaised,
]);

/** Above this many openings, a search box is drawn over the table (three fit without scrolling). */
const SEARCH_FROM = 3;

/** A row of the form: on a phone the label stands above its control, as in Raise hiring, so the budget and the openings get the full width. */
const ROW = 'grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-[110px_minmax(0,1fr)] sm:items-start';
const LABEL = 'pt-2 text-sm font-medium text-gray-600';
const PRIMARY = 'inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50';
const SECONDARY = 'rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50';
const TAG = 'inline-block whitespace-nowrap rounded-full px-2 py-px align-middle text-[11px] font-semibold';
/**
 * The table's own box. On a phone it scrolls sideways only (the table keeps its columns' width)
 * and the window's body scrolls it down to the last row. From a laptop's width it also scrolls
 * down, in a box sized to fit inside the body: 70vh is the body's height (Modal size '2xl') and
 * 16rem is what the body holds besides the table (its padding, Store, Budget, the search box,
 * the line under the table and the shared line), so the last row and the line under the table
 * are never cut off by the body's edge. Never under 10rem (a short screen: the body then scrolls
 * the rest). Nothing stops a scroll at the box's end from moving the body on (no overscroll).
 * The header row stays in sight while the rows scroll (scroll-pt keeps a focused row below it).
 */
const TABLE_BOX = 'overflow-x-auto scroll-pt-10 sm:max-h-[max(10rem,calc(70vh-16rem))] sm:overflow-y-auto';
/**
 * The table never squeezes its seven columns below this: narrower, its box scrolls sideways.
 * City and State have a fixed 7.5rem each, enough for an Indian city or State written as one
 * word ("Bengaluru", "Chhattisgarh", "Visakhapatnam") so it is never split in the middle; at
 * this width every other column keeps at least the room it had before they were fixed.
 */
const TABLE_MIN = 'min-w-[58rem]';
const TH = 'sticky top-0 z-10 whitespace-nowrap border-b border-gray-200 bg-gray-50 px-2.5 py-2 text-left text-xs font-semibold text-gray-500';
const TD = 'border-b border-gray-100 px-2.5 py-2.5 align-top leading-snug break-words';

const NONE_FOLLOWED: readonly HiringLink[] = [];

export function LinkHiringModal({
  projectId, subject, followed = NONE_FOLLOWED, initial, onClose, onLinked, onOutOfDate, onHiringGone,
}: {
  projectId: number;
  subject: LinkHiringSubject;
  /** The links the store already has, when the owner knows them: their openings are not offered again. */
  followed?: readonly HiringLink[];
  /**
   * Opened from "Link it instead" in the Raise window: the budget, the unlinked opening
   * Recruitment already has, and the store, chosen to begin with. The person still looks at
   * it and presses Link; nothing is linked by itself, and a pick the list does not offer is not made.
   */
  initial?: LinkHiringInitial;
  onClose: () => void;
  /** The link was saved: the owner closes the window and shows the new link. */
  onLinked: (link: HiringLink | undefined) => void;
  /**
   * The owner's list may be missing a link: the server said the opening is already
   * linked, or a link request got no answer and may have been saved all the same.
   */
  onOutOfDate?: () => void;
  /** Hiring was switched off for the project while the window was open. */
  onHiringGone: () => void;
}) {
  const fixedStore: LinkStore | null = subject.kind === 'store'
    ? { storeId: subject.storeId, storeName: subject.storeName, storeCode: subject.storeCode }
    : null;
  // From the dashboard: the store chosen in the window, if any.
  const [pickedStore, setPickedStore] = useState<LinkStore | null>(subject.kind === 'role' ? initial?.store ?? null : null);
  const store = fixedStore ?? pickedStore;
  const subjectKind: 'store' | 'role' = store ? 'store' : 'role';

  // The pick the window was opened with, until it has been made once (or cannot be).
  const preselect = useRef(initial ? { budgetId: initial.budgetId, openingId: initial.openingId } : null);

  const [budgets, setBudgets] = useState<HiringBudget[] | null>(null);
  const [budgetsError, setBudgetsError] = useState<string | null>(null);
  // A refusal that asking again cannot change: no "Try again" is offered under it.
  const [budgetsFinal, setBudgetsFinal] = useState(false);
  const [budgetReads, setBudgetReads] = useState(0);
  const [budgetId, setBudgetId] = useState('');

  const [openings, setOpenings] = useState<HiringPickerOpening[] | null>(null);
  const [openingsLoading, setOpeningsLoading] = useState(false);
  const [openingsError, setOpeningsError] = useState<string | null>(null);
  const [positionsPerOpening, setPositionsPerOpening] = useState(DEFAULT_POSITIONS_PER_OPENING);
  // Guards against a slow answer for a budget chosen earlier landing after the one on show.
  const openingsReq = useRef(0);
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  // Openings the server refused to link from this window, with the words for each row:
  // kept for as long as the window is open, since a fresh read can show them unchanged.
  const [refused, setRefused] = useState<ReadonlyMap<number, string>>(new Map());

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gone = useRef(onHiringGone);
  gone.current = onHiringGone;
  // Chooses a budget as a person would; set once chooseBudget exists, further down.
  const chooseFirst = useRef<(value: string) => void>(() => undefined);

  // The project's budgets, read once when the window opens (and on "Try again").
  useEffect(() => {
    let cancelled = false;
    setBudgets(null);
    setBudgetsError(null);
    setBudgetsFinal(false);
    Promise.resolve()
      .then(() => hiringLinksAPI.budgets(projectId))
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data?.budgets ?? [];
        setBudgets(list);
        // "Link it instead": the budget that was reviewed is chosen, when the project still has it.
        const wanted = preselect.current;
        if (wanted && list.some((b) => b.budgetId === wanted.budgetId)) chooseFirst.current(String(wanted.budgetId));
        else preselect.current = null;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isHiringOff(err)) gone.current();
        else {
          setBudgetsFinal(isNoRecruitmentIdentity(err));
          setBudgetsError(hiringErrorText(err, LINK_WORDS.budgetsFailed));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, budgetReads]);

  const readOpenings = useCallback((budget: string) => {
    const req = ++openingsReq.current;
    setOpeningId(null);
    setOpeningsError(null);
    if (!budget) {
      setOpenings(null);
      setOpeningsLoading(false);
      return;
    }
    setOpeningsLoading(true);
    Promise.resolve()
      .then(() => hiringLinksAPI.budgetOpenings(projectId, Number(budget)))
      .then((res) => {
        if (req !== openingsReq.current) return;
        const data = res?.data?.data;
        setOpenings(data?.openings ?? []);
        setPositionsPerOpening(positionsPerOpeningOf(data));
        setOpeningsLoading(false);
        // "Link it instead": that opening is ticked, once, when this budget's list has it.
        // Whether it may be linked is still decided row by row below.
        const wanted = preselect.current;
        if (wanted && String(wanted.budgetId) === budget) {
          preselect.current = null;
          if ((data?.openings ?? []).some((o) => o.openingId === wanted.openingId)) setOpeningId(wanted.openingId);
        }
      })
      .catch((err: unknown) => {
        if (req !== openingsReq.current) return;
        setOpeningsLoading(false);
        if (isHiringOff(err)) gone.current();
        else {
          setOpenings(null);
          setOpeningsError(hiringErrorText(err, LINK_WORDS.openingsFailed));
        }
      });
  }, [projectId]);

  // A late answer must not land in a window that has closed.
  useEffect(() => () => {
    openingsReq.current += 1;
  }, []);

  const chooseBudget = (value: string) => {
    setBudgetId(value);
    setSearch('');
    setError(null);
    readOpenings(value);
  };
  chooseFirst.current = chooseBudget;

  const chooseStore = (next: LinkStore | null) => {
    setPickedStore(next);
    setError(null);
    // A refusal said of one store is not true of another.
    setRefused(new Map());
  };

  const followedOpenings = useMemo(() => {
    const ids = new Set<number>();
    if (subject.kind === 'store') for (const link of followed) if (link.state !== 'closed' && link.openingId != null) ids.add(link.openingId);
    return ids;
  }, [followed, subject.kind]);
  // Openings that can be linked with a position open first, then those with an offer out, then
  // the greyed ones; Recruitment's own order within each.
  const rows = useMemo(
    () => (openings ?? [])
      .map((opening, at) => {
        const pick = pickState(opening, subjectKind, positionsPerOpening, refused, followedOpenings);
        return { opening, pick, at, rank: linkOpeningRank(opening, pick) };
      })
      .sort((a, b) => a.rank - b.rank || a.at - b.at),
    [openings, subjectKind, positionsPerOpening, refused, followedOpenings],
  );
  // Every opening that matches is drawn (the table scrolls): nothing is cut off (15 Oct 2026).
  const visible = rows.filter((r) => linkOpeningMatches(r.opening, search));
  // Looked up in every row, not only those drawn: a pick outlives a search.
  const picked = rows.find((r) => r.opening.openingId === openingId && r.pick.pickable) ?? null;

  /** Picks an opening, by a click anywhere on its row, its radio, or Space / Enter on it. Greyed rows pick nothing. */
  const pickOpening = (id: number) => {
    if (saving || !rows.some((r) => r.opening.openingId === id && r.pick.pickable)) return;
    setOpeningId(id);
    setError(null);
  };

  /**
   * The keyboard on the table's radios (radio semantics): the arrow keys move to the next or the
   * previous row that can be picked (Home and End to the first and the last), without picking it;
   * Space or Enter picks the row the focus is on. Greyed rows are passed over: their radios are
   * disabled. The browser's own arrow-key move between radios (which picks as it moves) is not used.
   */
  const onRadioKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const on = e.target;
    if (!(on instanceof HTMLInputElement) || on.type !== 'radio') return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      pickOpening(Number(on.value));
      return;
    }
    const radios = [...e.currentTarget.querySelectorAll<HTMLInputElement>('input[type="radio"]:not(:disabled)')];
    const at = radios.indexOf(on);
    if (at < 0) return;
    const moves: Partial<Record<string, number>> = {
      ArrowDown: at + 1, ArrowRight: at + 1, ArrowUp: at - 1, ArrowLeft: at - 1, Home: 0, End: radios.length - 1,
    };
    const to = moves[e.key];
    if (to === undefined) return;
    e.preventDefault();
    radios[Math.max(0, Math.min(radios.length - 1, to))].focus();
  };

  const canSave = !!budgetId && !!picked && !saving;

  const save = async () => {
    if (!canSave || !picked) return;
    const body: HiringLinkInput = {
      // With no store, no name is sent: the server names it after its budget.
      subject: store ? { kind: 'store', storeId: store.storeId } : { kind: 'role' },
      budgetId: Number(budgetId),
      openingId: picked.opening.openingId,
      shared: picked.pick.shared,
    };
    setSaving(true);
    setError(null);
    try {
      const res = await hiringLinksAPI.link(projectId, body);
      onLinked(res?.data?.data);
    } catch (err) {
      if (isHiringOff(err)) {
        onHiringGone();
        return;
      }
      setError(hiringErrorText(err, LINK_WORDS.failed));
      const code = hiringCodeOf(err);
      const tag = refusedOpeningTag(code);
      if (tag) setRefused((prev) => new Map(prev).set(body.openingId, tag));
      if (code && REREAD_OPENINGS_ON.has(code)) readOpenings(budgetId);
      // Already linked - perhaps by this very window, on a try whose answer never came - or
      // no answer at all this time: the owner's list is read again either way.
      if (code === HIRING_CODES.alreadyLinked || hiringStatusOf(err) === undefined) onOutOfDate?.();
      setSaving(false);
    }
  };

  const header = (
    <div className="flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-4">
      <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
        {LINK_WORDS.title}
        <span
          title={LINK_WORDS.info}
          aria-label={LINK_WORDS.info}
          role="img"
          className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-400 text-[10px] font-semibold not-italic text-gray-500"
        >
          i
        </span>
      </h3>
      <button type="button" aria-label="Close" onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
        <X className="h-5 w-5" />
      </button>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={LINK_WORDS.title}
      headerContent={header}
      size="2xl"
      footer={(
        <div className="flex w-full flex-col gap-2">
          {/* In the footer, not the scrolling body: a refusal must be seen from row 300 too. */}
          {error && <FormErrorBanner message={error} />}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/*
              Left: the opening that will be linked, once one is picked - its address, then its team,
              since openings at one address differ only in their team. Shown while a search hides
              its row too. One line from a laptop's width ("…" when long, whole on its hover, with
              the opening's number); on a phone it wraps above the buttons.
            */}
            {picked && (
              <p className="min-w-0 break-words text-[13px] text-gray-600 sm:flex-1 sm:truncate" title={linkChosenHover(picked.opening)}>
                {`${LINK_WORDS.chosen} `}
                <span className="font-semibold text-gray-900">{openingAddress(picked.opening)}</span>
                {` · ${linkChosenTeam(picked.opening)}`}
              </p>
            )}
            <div className="flex shrink-0 items-center justify-end gap-3 sm:ml-auto">
              <button type="button" className={SECONDARY} onClick={onClose} disabled={saving}>{LINK_WORDS.cancel}</button>
              <button
                type="button"
                aria-label={LINK_WORDS.linkName(store ? linkStoreLabel(store, false) : null)}
                className={PRIMARY}
                onClick={() => void save()}
                disabled={!canSave}
              >
                {saving && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
                {LINK_WORDS.link}
              </button>
            </div>
          </div>
        </div>
      )}
    >
      <div className="space-y-3">
        <div className={ROW}>
          <label className={LABEL} htmlFor="link-hiring-store">
            {LINK_WORDS.store}
            {!fixedStore && <span className="block text-xs font-normal leading-tight text-gray-400">{` ${LINK_WORDS.optional}`}</span>}
          </label>
          {fixedStore ? (
            <output id="link-hiring-store" className="block truncate pt-2 text-sm font-medium text-gray-900">{linkStoreLabel(fixedStore, false)}</output>
          ) : (
            <LinkStorePicker
              id="link-hiring-store"
              projectId={projectId}
              value={pickedStore}
              onChange={chooseStore}
              disabled={saving}
              onRefused={(err) => {
                if (!isHiringOff(err)) return false;
                gone.current();
                return true;
              }}
            />
          )}
        </div>

        <div className={ROW}>
          <label className={LABEL} htmlFor="link-hiring-budget">{LINK_WORDS.budget}</label>
          <div className="min-w-0">
            {budgetsError ? (
              <div className="space-y-2">
                <FormErrorBanner message={budgetsError} />
                {!budgetsFinal && (
                  <button type="button" className="text-sm text-primary-600 hover:underline" onClick={() => setBudgetReads((n) => n + 1)}>
                    {LINK_WORDS.tryAgain}
                  </button>
                )}
              </div>
            ) : budgets === null ? (
              <InlineLoader label={LINK_WORDS.budgetsLoading} className="flex pt-2" />
            ) : budgets.length === 0 ? (
              <p className="pt-2 text-sm text-gray-500">{LINK_WORDS.noBudgets}</p>
            ) : (
              <SearchableSelect
                id="link-hiring-budget"
                value={budgetId}
                onChange={chooseBudget}
                options={budgets.map((b) => ({ value: String(b.budgetId), label: b.budgetName }))}
                placeholder={LINK_WORDS.budgetPlaceholder}
                searchPlaceholder="Search budgets"
                allowClear={false}
                disabled={saving}
              />
            )}
          </div>
        </div>

        <div className={ROW}>
          <span className={LABEL}>{LINK_WORDS.opening}</span>
          <div className="min-w-0 space-y-2">
            {!budgetId ? (
              <p className="pt-2 text-sm text-gray-500">{LINK_WORDS.chooseBudgetFirst}</p>
            ) : openingsError ? (
              <div className="space-y-2">
                <FormErrorBanner message={openingsError} />
                <button type="button" className="text-sm text-primary-600 hover:underline" onClick={() => readOpenings(budgetId)}>
                  {LINK_WORDS.tryAgain}
                </button>
              </div>
            ) : openingsLoading || openings === null ? (
              <InlineLoader label={LINK_WORDS.openingsLoading} className="flex pt-2" />
            ) : openings.length === 0 ? (
              <p className="pt-2 text-sm text-gray-500">{LINK_WORDS.noOpenings}</p>
            ) : (
              <>
                {rows.length > SEARCH_FROM && (
                  <div className="relative">
                    <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      aria-label={LINK_WORDS.searchOpenings}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={LINK_WORDS.searchOpeningsPlaceholder}
                      className="h-9 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                )}
                {visible.length === 0 ? (
                  <p className="text-sm text-gray-500">{LINK_WORDS.noOpeningMatches}</p>
                ) : (
                  <>
                    {/*
                      One row per opening, as one choice. The window's body (Modal '2xl': at most 70vh)
                      scrolls, and holds the store, the budget and the search above the table. The
                      table's box (TABLE_BOX) scrolls sideways on a phone, where the body alone scrolls
                      it down; from a laptop's width it scrolls down too, sized to fit inside the body,
                      and scrolling on past its end carries on into the body, so the last row and the
                      line under the table are always reached.
                    */}
                    <div
                      role="radiogroup"
                      aria-label={LINK_WORDS.openingsGroup}
                      onKeyDown={onRadioKey}
                      className={`rounded-lg border border-gray-200 bg-white ${TABLE_BOX}`}
                    >
                      <table className={`w-full table-fixed border-separate border-spacing-0 text-[13px] ${TABLE_MIN}`}>
                        <colgroup>
                          <col className="w-10" />
                          <col className="w-[22%]" />
                          <col className="w-[7.5rem]" />
                          <col className="w-[7.5rem]" />
                          <col className="w-[5.5rem]" />
                          <col className="w-[14%]" />
                          <col className="w-[12%]" />
                          <col />
                        </colgroup>
                        <thead>
                          <tr>
                            <th scope="col" className={TH}><span className="sr-only">{LINK_WORDS.columns.choose}</span></th>
                            <th scope="col" className={TH}>{LINK_WORDS.columns.address}</th>
                            <th scope="col" className={TH}>{LINK_WORDS.columns.city}</th>
                            <th scope="col" className={TH}>{LINK_WORDS.columns.state}</th>
                            {/* Named "Open" alone, so each cell is read with a short header; what Open means is its (i). */}
                            <th scope="col" aria-label={LINK_WORDS.columns.open} className={TH}>
                              <span className="inline-flex items-center gap-1">
                                {LINK_WORDS.columns.open}
                                <span
                                  role="img"
                                  aria-label={OPENING_ROW_WORDS.openHover}
                                  title={OPENING_ROW_WORDS.openHover}
                                  className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-gray-400 text-[9px] font-semibold text-gray-500"
                                >
                                  i
                                </span>
                              </span>
                            </th>
                            <th scope="col" className={TH}>{LINK_WORDS.columns.recruiters}</th>
                            <th scope="col" className={TH}>{LINK_WORDS.columns.offerMakers}</th>
                            <th scope="col" className={TH}>{LINK_WORDS.columns.interviewers}</th>
                          </tr>
                        </thead>
                        <tbody className="[&>tr:last-child>td]:border-b-0">
                          {visible.map(({ opening, pick }) => (
                            <OpeningRow
                              key={opening.openingId}
                              opening={opening}
                              pickable={pick.pickable}
                              tag={pick.tag}
                              word={openingTagWord(pick)}
                              on={openingId === opening.openingId && pick.pickable}
                              disabled={!pick.pickable || saving}
                              onPick={() => pickOpening(opening.openingId)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500">
                      {LINK_WORDS.openingsShown(visible.length, search.trim() !== '', visible.some((r) => !r.pick.pickable))}
                    </p>
                  </>
                )}
                {picked?.pick.shared && <p className="text-xs text-gray-600">{sharedLine(picked.opening)}</p>}
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/** One name per line in a cell, or "–" when there is none. */
function Names({ names }: { names: readonly string[] }) {
  if (names.length === 0) return <>{OPENING_ROW_WORDS.nobody}</>;
  return <>{names.map((name, i) => <span key={`${i}:${name}`} className="block">{name}</span>)}</>;
}

/**
 * One opening, as a row of the table the person picks (approved option A, 16 Oct 2026):
 *   ( )  Address [word] | City | State | "2 of 4" | Recruiters | Offer makers | Interviewers
 * A native radio, named "Opening 48213, <address>" for a screen reader, in the first cell; a
 * click anywhere on a row that can be picked picks it. Names one per line; interviewers as
 * "R1 Name", "R2 Client"; "–" for a place or a role that is not known. Open is a pill on a row
 * that can be picked: green with a position open, grey at "0 of N". A row that cannot be picked
 * is greyed with a short word in its Address cell and the reason on hover. The row's hover starts with the opening's number.
 */
function OpeningRow({
  opening, pickable, tag, word, on, disabled, onPick,
}: {
  opening: HiringPickerOpening;
  pickable: boolean;
  tag: string | null;
  word: string | null;
  on: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  const address = openingAddress(opening);
  const rounds = openingRounds(opening.team ?? null);
  const open = OPENING_ROW_WORDS.openOf(opening.open, opening.positions);
  return (
    <tr
      title={`${OPENING_ROW_WORDS.hover(opening.openingId)}${tag ? ` · ${tag}` : ''}`}
      onClick={pickable ? (e) => {
        onPick();
        // The focus follows the pick, so the arrow keys go on from this row.
        const radio = e.currentTarget.querySelector('input');
        if (radio && !radio.disabled && document.activeElement !== radio) radio.focus({ preventScroll: true });
      } : undefined}
      className={on ? 'bg-primary-50' : pickable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed text-gray-400'}
    >
      <td className={`${TD} text-center`}>
        <input
          type="radio"
          name="link-hiring-opening"
          value={opening.openingId}
          aria-label={OPENING_ROW_WORDS.radioName(opening.openingId, address)}
          className="mt-0.5 h-4 w-4 accent-primary-600 disabled:cursor-not-allowed"
          checked={on}
          disabled={disabled}
          onChange={onPick}
        />
      </td>
      <td className={`${TD} ${pickable ? 'text-gray-900' : ''}`}>
        {address}
        {/* A space, so the word is read apart from the address. */}
        {word && ' '}
        {word && (
          <span title={tag ?? undefined} className={`${TAG} ml-1 ${pickable ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-500'}`}>{word}</span>
        )}
      </td>
      <td className={`${TD} ${pickable ? 'text-gray-700' : ''}`}>{openingPlaceCell(opening.cityName)}</td>
      <td className={`${TD} ${pickable ? 'text-gray-700' : ''}`}>{openingPlaceCell(opening.stateName)}</td>
      <td className={`${TD} whitespace-nowrap tabular-nums`}>
        {/* Green only with a position open: "0 of 2" on a row that can still be picked (an offer out, or a shared opening) is grey. */}
        {pickable
          ? <span className={`inline-block rounded-full px-2 py-px text-xs font-semibold ${opening.open > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{open}</span>
          : open}
      </td>
      <td className={`${TD} ${pickable ? 'text-gray-700' : ''}`}><Names names={openingNames(opening.team?.recruiters)} /></td>
      <td className={`${TD} ${pickable ? 'text-gray-700' : ''}`}><Names names={openingNames(opening.team?.offerMakers)} /></td>
      <td className={`${TD} ${pickable ? 'text-gray-700' : ''}`}>
        {rounds.length === 0 ? OPENING_ROW_WORDS.nobody : rounds.map((r) => (
          <span key={r.round} className="block">
            <span title={OPENING_ROW_WORDS.roundHover(r.round)} className="text-[11px] font-semibold text-gray-400">{r.label}</span>
            {` ${r.who}`}
          </span>
        ))}
      </td>
    </tr>
  );
}

export default LinkHiringModal;
